import { Container, Title, Text, Box, List } from "@mantine/core";

export default function TermsPage() {
  return (
    <Box py={60} bg="white" style={{ minHeight: "60vh" }}>
      <Container size="md">
        <Title order={1} mb="xl" c="primary.6">
          Terms and Conditions
        </Title>

        <Box c="slate.8" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Text lh={1.6}>
            These Terms and Conditions (<strong>"Terms"</strong>) govern access to and use of the website, platform,
            applications, tools, including any content, functionality, and services offered on or through{" "}
            <em>www.gtsetu.com</em> (collectively, the <strong>"Platform"</strong>) operated by{" "}
            <strong>GT Setu Connections Pvt Ltd</strong> (<strong>"Company"</strong>, <strong>"we"</strong>,{" "}
            <strong>"our"</strong>, or <strong>"us"</strong>). The Platform aims to operate as a business-to-business
            (B2B) matchmaking and collaboration service that enables entities and their authorised representatives to
            discover, evaluate, and connect with other entities for potential Technical and commercial collaborations.
          </Text>

          <Text lh={1.6}>
            These Terms are legally binding on and apply to all individuals or entities that access, browse, register
            on, or use the Platform (<strong>"User Entity(ies)"</strong>, and their Authorized Representatives
            (collectively, <strong>"you"</strong> or <strong>"your"</strong>)). For the purpose of these Terms,{" "}
            <strong>"Authorised Representatives"</strong> shall refer to individuals who access or use the Platform on
            behalf of a User Entity and has the authority to bind such User Entity to these Terms.
          </Text>

          <Text lh={1.6}>
            By accessing or using the Platform, you acknowledge that they have read, understood, and agree to be bound
            by these Terms, along with any policies or guidelines referenced herein or made available on the Platform
            from time to time.
          </Text>

          <Title order={3} mt="xl">
            1. ACCEPTANCE OF TERMS
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                You acknowledge and agree that by accessing, browsing, registering on, or otherwise using the Platform
                in any manner, you are bound by these Terms. Such acceptance shall constitute a legally binding
                agreement between you and the Company, regardless of whether you create an account or complete a
                registration process.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You understand and agree that acceptance of these Terms may occur through electronic means, including by
                clicking an acceptance button, completing a registration process, or by continued access to or use of
                the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right to update or modify these Terms from time to time in accordance with the
                applicable amendment provisions. Continued access to or use of the Platform following any such updates
                shall constitute acceptance of the revised Terms.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            2. ELIGIBILITY AND AUTHORITY
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                Access to and use of the Platform is available only to User Entities and Authorized Representatives
                acting in a professional or commercial capacity on behalf of such User Entities.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>By accessing or using the Platform, you represent and warrant that:</Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>
                    you are at least eighteen (18) years of age and have the legal capacity to enter into binding
                    agreements; you are a legally recognized business entity or an authorized representative of such
                    entity;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    you are duly authorised to act for and bind such entity to these Terms and any obligations arising
                    from use of the Platform;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    all information provided to the Company during registration, verification, or use of the Platform is
                    true, accurate, current, and complete, and that such information will be kept updated as necessary;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>your use of the Platform complies with all applicable laws and regulations.</Text>
                </List.Item>
              </List>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            3. SCOPE OF SERVICES
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Platform provides technology-driven business-to-business (B2B) matchmaking and collaboration
                services, including entity discovery, profile creation, verification, interest-based matching, paid
                access to entity details, document exchange, and related facilitation services (
                <strong>"Services"</strong>).
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>The Services offered through the Platform may include, without limitation:</Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>browsing and viewing anonymised or limited entity listings;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>registration and creation of entity profiles;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    verification of entity details based on information and documents provided by Authorised
                    Representatives and from lawful sources;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    expressing and receiving interest between entities based on sector, geography, or other criteria;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    paid access to detailed entity information upon fulfilment of applicable requirements;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>facilitation of communication and interaction between matched entities;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    exchange of documents and information through the Platform, including technical, commercial,
                    financial, and legal materials, subject to access controls and, where applicable, non-disclosure
                    agreements;
                  </Text>
                </List.Item>
              </List>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company may modify, enhance, suspend, or discontinue any aspect of the Services from time to time.
                The Company does not guarantee that any particular result, collaboration, transaction, or business
                outcome will be achieved through use of the Platform.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            4. REGISTRATION AND PROFILE GENERATION
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                Registration on the Platform is available at no cost and requires the submission of certain basic
                corporate and contact information, including, without limitation:
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>the legal name and registered address of the User Entity;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>the applicable User Entity Identification/Registration Number;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>the User Entity's official website and/or corporate profile;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    a valid contact telephone number, which shall be authenticated through a one-time password (OTP)
                    verification mechanism; and
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>details of the Authorized Representative of the User Entity.</Text>
                </List.Item>
              </List>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Upon successful completion of the registration process, the Platform shall generate a profile for the
                User Entity. Such profile shall be generated through automated systems, including artificial
                intelligence tools and algorithms, using the information submitted by you and, where applicable,
                information obtained from lawful, publicly available sources. The generated profile shall include a
                system-generated, random reference number, which may be used for identification, tracking, and
                referencing purposes within the Platform and in communications facilitated through the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You acknowledge and agree that any profile generated through automated or artificial intelligence driven
                processes is produced on the basis of available data and predefined parameters and may contain
                inaccuracies, omissions, or assumptions. The Company does not warrant or represent that such
                automatically generated profiles are complete, accurate, up to date, or fit for any particular purpose.
                You remain solely responsible for reviewing, verifying, and, where applicable, correcting the
                information displayed in its profile.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            5. OBLIGATIONS OF USER ENTITIES
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                To access certain features of the Platform, you may be required to create and maintain an account. You
                agree to provide accurate, current, and complete information during account creation and to keep such
                information updated at all times.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You shall be solely responsible for maintaining the confidentiality of your account credentials and for
                all activities, actions, and communications carried out through its account conducted through their
                account, whether authorised or unauthorised. You shall promptly notify the Company of any actual or
                suspected unauthorised access to or use of your account. You agree to use the Platform solely for lawful
                purposes and in accordance with these Terms and all applicable laws.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You shall not permit any third party to access or use their account without proper authorisation.
                Company shall not be liable for any loss, damage, or claim arising from the your failure to safeguard
                its account credentials or from reliance placed by you or any third party on information generated or
                displayed through your account.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right to suspend, restrict, or terminate accounts that are found to be in
                breach of these Terms, which contain inaccurate or misleading information, or that pose a risk to the
                security or integrity of the Platform.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            6. VERIFICATION
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Platform may offer verification features to assist User Entities in identifying and engaging with
                other entities. Verification is carried out based on information, documents, and declarations provided
                by you and, where applicable, information obtained from statutory records and publicly available
                sources.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>Verification may include, where applicable:</Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>User Entity name and registered address;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Applicable User Entity Identification/Registration Number;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Contact details and email address;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Date of incorporation;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Industry classification;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Legal structure (e.g., sole proprietorship, partnership, LLP, private or public limited company);
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Capital structure and statutory filings;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Director and charge details.</Text>
                </List.Item>
              </List>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Where any information or documents are submitted by an Authorized Representative on behalf of a User
                Entity, such Authorized Representative shall be required to upload a valid authority letter or power of
                attorney, issued on the User Entity's official letterhead, duly executed and bearing the User Entity's
                corporate seal (where applicable), together with submission of such documents from the User Entity's
                officially registered email address. The User Entity shall remain fully responsible for all information
                and documents submitted by its Authorized Representative.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Upon successful verification, the User Entity may be granted a "verified" status. You acknowledge and
                agree that verification is intended solely as a facilitative measure and does not constitute an
                endorsement, certification, guarantee, or warranty by the Company regarding the legal standing,
                creditworthiness, performance, or reliability of any User Entity.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You represent and warrant that all information and documents submitted for registration, verification,
                or continued use of the Platform are true, accurate, complete, and lawful, and that they have the right
                and authority to disclose such information.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            7. TRANSACTION BETWEEN ENTITIES
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                Subject to compliance with these Terms and the applicable verification requirements of the Platform,
                registered and verified User Entities shall be entitled to express an indication of interest in other
                entities listed on the Platform, including on the basis of sector, geographic location, or such other
                search parameters and filters as may be made available on the Platform from time to time.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Upon receipt of an indication of interest any payment of applicable Platform fees, the recipient entity
                shall be permitted to access and review the revealed profile-level information of the expressing entity
                at a level substantially equivalent to that available to the expressing entity, and shall have the sole
                discretion to accept or reject such indication of interest, without any obligation to provide reasons.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Platform acts solely as a technology-based intermediary that facilitates discovery, communication,
                and interaction between User Entities. The Company is not a party to, and does not participate in, any
                negotiations, agreements, transactions, or contractual relationships entered into between two User
                Entities or other collaborating entities through or outside the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                All commercial terms, negotiations, representations, warranties, obligations, and performance relating
                to any transaction or collaboration are agreed upon solely between the relevant User Entities. User
                Entities acknowledge and agree that any transaction is undertaken at their own discretion and risk.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company does not control, verify, or guarantee the quality, legality, safety, delivery, performance,
                or outcome of any products, services, or collaborations offered or entered into by User Entities. The
                Company shall not be responsible or liable for any disputes, claims, losses, damages, or liabilities
                arising out of or in connection with transactions or collaborations between User Entities.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                User Entities are solely responsible for complying with all applicable laws, regulations, and
                contractual obligations in relation to their transactions, including tax, regulatory, and compliance
                requirements.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            8. FEES, PAYMENTS AND TAXES
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                Access to certain features and services of the Platform (collectively, the{" "}
                <strong>"Paid Services"</strong>
                ), including verified connectivity, access to expanded information, structured communications, document
                exchange, and platform-generated reports, is available only to registered and, where applicable,
                verified User Entities, and is subject to payment of the applicable fees displayed on the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Before enabling or purchasing any Paid Service, a User Entity will be given an opportunity to review the
                applicable fees and payment terms. By confirming the purchase or activation of a Paid Service, the User
                Entity agrees to pay all applicable fees within two (2) days of expressing interest to view the revealed
                profile.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Certain Paid Services may require payment by more than one User Entity, including services that enable
                mutual connectivity or collaboration. Where a User Entity completes payment but the corresponding
                counterparty fails to complete its payment within the specified timeframe, the amount paid by the first
                User Entity shall be credited to the User Entity's Platform wallet in the form of points or wallet
                balance. Such credits may be used only for future Paid Services on the Platform and are not redeemable
                for cash.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right, at its discretion, to revise fees, introduce new charges, or modify,
                suspend, or discontinue any Paid Service or feature of the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                All fees are exclusive of applicable taxes, duties, or governmental charges, which shall be borne solely
                by the User Entity. The Company shall not be responsible for collecting or remitting such taxes, except
                as required by applicable law.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                If a User Entity's payment fails or is reversed for any reason, the Company may suspend or restrict
                access to the relevant Paid Service and require payment of any outstanding amounts, including applicable
                third-party processing or chargeback fees.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Except as expressly stated in these Terms or required by law, all fees paid are non-refundable and
                non-cancellable.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                User Entities represent and warrant that all information provided in connection with registration,
                verification, or payment for Paid Services is true, accurate, current, and complete. The Company may
                verify such information and suspend, cancel, or limit access to any Paid Service if the information is
                inaccurate, misleading, or cannot be verified.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Payments may be processed through third-party payment service providers. The Company does not store
                complete payment instrument details and shall not be responsible for any issues arising from the
                processing of payments by such third-party providers.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            9. CONFIDENTIALITY
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                You acknowledge that, in the course of using the Platform, you may receive or access any non-public
                information disclosed by one entity to another, including technical, commercial, financial, or legal
                information.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You agree to maintain the confidentiality of such information and to use it solely for the purpose for
                which it was disclosed. Confidential information shall not be disclosed to any third party except as
                permitted under applicable non-disclosure agreements or as required by law.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Platform may facilitate the execution and exchange of non-disclosure agreements (NDAs) between
                entities to protect the confidentiality of the information shared by each entity. Any such NDA shall be
                entered into solely between the relevant User Entities, and the Company shall not be a party to, nor
                assume any rights or obligations under, such NDA. Where an NDA is executed, the terms of such NDA shall
                govern the handling and disclosure of confidential information between the relevant User Entities.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company shall not be responsible for the misuse, unauthorised disclosure, or breach of
                confidentiality by any entities. You remain solely responsible for ensuring compliance with their
                confidentiality and non-disclosure obligations.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            10. PROHIBITED ACTIVITIES
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                You agree to use the Platform in a lawful, professional, and responsible manner and in compliance with
                these Terms and all applicable laws and regulations.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>You shall not, directly or indirectly:</Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>
                    provide false, misleading, or inaccurate information, including during registration or verification;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>impersonate any person or entity or misrepresent affiliation or authority;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>use the Platform for fraudulent, unlawful, deceptive, or harmful activities;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    upload, share, or transmit any content that is illegal, offensive, defamatory, infringing, or
                    otherwise objectionable;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    interfere with, disrupt, or compromise the security, integrity, or proper functioning of the
                    Platform;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    attempt to gain unauthorised access to the Platform, other entity accounts, or systems connected to
                    the Platform;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    scrape, copy, reverse engineer, or misuse Platform data, content, or technology without
                    authorisation;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    circumvent or attempt to circumvent any access controls, payment mechanisms, or usage restrictions
                    implemented by the Company.
                  </Text>
                </List.Item>
              </List>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right to investigate any suspected violation of this clause and to take
                appropriate action, including suspension or termination of access to the Platform, without prejudice to
                any other rights or remedies available to the Company.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            11. INTELLECTUAL PROPERTY RIGHTS
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                All intellectual property rights in and to the Platform, including its software, design, layout,
                databases, text, graphics, logos, trademarks, and other content, are owned by or licensed to the Company
                and are protected under applicable intellectual property laws.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Subject to compliance with these Terms, the Company grants you a limited, non-exclusive,
                non-transferable, and revocable licence to access and use the Platform solely for legitimate business
                purposes in connection with the Services.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You shall not copy, reproduce, modify, distribute, display, publish, transmit, reverse engineer,
                decompile, or otherwise exploit any part of the Platform or its content without the prior written
                consent of the Company, except as expressly permitted under these Terms or applicable law.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You retain ownership of the content and information they submit to the Platform; however, by submitting
                such content, You grant the Company a limited licence to host, store, process, display, and use such
                content solely for the purpose of operating and providing the Platform and its Services.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Nothing in these Terms shall be construed as transferring any intellectual property rights from the
                Company to you or any third party.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            12. THIRD-PARTY SERVICES AND INTEGRATIONS
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Platform may integrate with or provide access to third-party services, tools, or platforms,
                including but not limited to verification providers, payment gateways, cloud hosting services, analytics
                tools, and communication services (<strong>"Third-Party Services"</strong>). Such Third-Party Services
                are provided solely for your convenience.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company does not control and is not responsible for the availability, accuracy, content, security,
                performance, or privacy practices of any Third-Party Services. Use of Third-Party Services is subject to
                the terms, conditions, and privacy policies of the respective third parties, and you acknowledge that
                their interactions with such third parties are at their own risk.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company shall not be liable for any loss, damage, or dispute arising out of or in connection with
                the use of or reliance on any Third-Party Services, including any failure, breach of data, interruption,
                error, or omission attributable to such services.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            13. PRIVACY AND DATA PROTECTION
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The collection, use, storage, disclosure, and processing of personal data and business-related
                information through the Platform are governed by the Company's Privacy Policy, which forms an integral
                part of these Terms.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                By accessing or using the Platform, User Entities acknowledge that they have read and understood the
                Privacy Policy and consent to the processing of information in accordance with its terms. User Entities
                agree to comply with applicable data protection and privacy laws in relation to any information obtained
                through the Platform, including information relating to other User Entities.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company processes information only as described in the Privacy Policy and does not assume
                responsibility for the data protection practices of User Entities in relation to information exchanged
                between them.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            14. ANTI-CIRCUMVENTION
          </Title>

          <Text lh={1.6}>
            You acknowledge that the Platform, including its discovery, verification, interest expression, and
            connectivity features, constitutes proprietary infrastructure and commercial value created by the Company.
            Accordingly, you agree that it shall not, directly or indirectly, circumvent, bypass, avoid, or attempt to
            circumvent the Platform or Company for the purpose of initiating, continuing, or concluding discussions,
            collaborations, transactions, or engagements with another company first identified or introduced through the
            Platform.
          </Text>

          <Title order={3} mt="xl">
            15. DISCLAIMERS AND LIMITATION OF LIABILITY
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Platform and the Services are provided on an "as is" and "as available" basis. To the maximum extent
                permitted by applicable law, the Company disclaims all warranties, representations, and conditions,
                whether express, implied, or statutory, including any implied warranties of merchantability, fitness for
                a particular purpose, accuracy, completeness, reliability, or non-infringement.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company does not warrant that the Platform will be uninterrupted, error-free, secure, or free from
                defects, or that any information obtained through the Platform will be accurate, complete, or reliable.
                User Entities acknowledge that use of the Platform is at their own risk.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company does not act as a buyer, seller, agent, broker, partner, or representative of any User
                Entity, and does not participate in or control negotiations, transactions, or contractual relationships
                between the User Entities using the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                To the maximum extent permitted by applicable law, the Company shall not be liable for any indirect,
                incidental, special, consequential, or punitive damages, including loss of profits, revenue, business
                opportunities, data, or goodwill, arising out of or in connection with the use of or inability to use
                the Platform or Services.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Without prejudice to the foregoing, the Company's total aggregate liability for any claims arising out
                of or relating to these Terms or the use of the Platform shall be limited to the amount of fees, if any,
                paid by the relevant User Entity to the Company for the relevant Services during the twelve (12) months
                preceding the event giving rise to the claim.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            16. INDEMNIFICATION
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                You agree to indemnify, defend, and hold harmless the Company, its affiliates, directors, officers,
                employees, agents, and service providers from and against any and all claims, demands, losses, damages,
                liabilities, costs, and expenses (including reasonable legal fees) arising out of or in connection with:
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>Your access to or use of the Platform or Services;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    any information, content, or documents submitted, shared, or exchanged by the User Entity through
                    the Platform;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>any transaction, negotiation, or collaboration entered into with another entity;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>any breach of these Terms, the Privacy Policy, or applicable laws by you; or</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>any misrepresentation, misconduct, or unlawful act caused by you.</Text>
                </List.Item>
              </List>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right, at its own expense, to assume the exclusive defence and control of any
                matter subject to indemnification, in which case you agree to cooperate fully with the Company in
                asserting any available defences.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            17. SUSPENSION AND TERMINATION
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right, at its sole discretion and without prior notice, to suspend, restrict,
                or terminate a your access to the Platform, in whole or in part, if the Company reasonably believes that
                you have breached these Terms, violated applicable laws, provided false or misleading information,
                engaged in misconduct, or poses a risk to the security, integrity, or operation of the Platform.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right to restrict, suspend, or terminate access to the Platform where a User
                Entity does not meet the eligibility requirements or where the Company reasonably believes that the
                Authorised Representative lacks the authority to act on behalf of the relevant User Entity.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right to refuse, revoke, suspend, or modify your verification status at any
                time if information provided is found to be inaccurate, misleading, outdated, or in violation of these
                Terms or applicable laws.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You may discontinue use of the Platform or request account termination at any time, subject to the
                fulfilment of any outstanding obligations, including payment obligations and confidentiality
                requirements.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Upon suspension or termination, your right to access and use the Platform and its Services shall
                immediately cease. Certain provisions of these Terms, including but not limited to those relating to
                confidentiality, intellectual property, disclaimers, limitation of liability, indemnification, dispute
                resolution and governing law, shall survive suspension or termination.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            18. FORCE MAJEURE
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Company shall not be liable for any failure or delay in the performance of its obligations under
                these Terms where such failure or delay results from events beyond its reasonable control (
                <strong>"Force Majeure Events"</strong>).
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Force Majeure Events may include, without limitation, acts of God, natural disasters, epidemics or
                pandemics, war, terrorism, civil unrest, strikes or labour disputes, governmental actions or orders,
                power or internet outages, system failures, cyber-attacks, or failure of third-party service providers.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                During the continuance of a Force Majeure Event, the Company's obligations under these Terms shall be
                suspended to the extent affected by such event, and the Company shall resume performance as soon as
                reasonably practicable after the Force Majeure Event ceases.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            19. MISCELLANEOUS
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                The Company reserves the right to modify, amend, or update these Terms at any time, at its sole
                discretion, to reflect changes in legal requirements, business practices, or the operation of the
                Platform. Any amendments to these Terms shall be effective upon posting the revised version on the
                Platform, unless otherwise specified. The date of the latest update may be indicated on the Platform.
                You are encouraged to review these Terms periodically. Continued access to or use of the Platform after
                the effective date of any amendments shall constitute acceptance of the updated Terms.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The relationship between you and the Company is that of independent contractors. Nothing contained in
                this Agreement shall be construed as creating any agency, partnership, joint venture or other form of
                joint enterprise, employment, or fiduciary relationship between you and the Company.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                These Terms constitute the entire agreement between you and the Company with respect to access to and
                use of the Platform and supersede all prior or contemporaneous understandings, communications, or
                agreements, whether written or oral, relating to the subject matter hereof.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                If any provision of these Terms is held to be invalid, illegal, or unenforceable under applicable law,
                such provision shall be severed or limited to the minimum extent necessary, and the remaining provisions
                shall continue in full force and effect.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                The failure or delay of the Company to enforce any right or provision under these Terms shall not
                constitute a waiver of such right or provision.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                You may not assign or transfer their rights or obligations under these Terms without the prior written
                consent of the Company. The Company may assign or transfer these Terms in connection with a merger, sale
                of assets, or reorganisation.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                These Terms shall apply for the duration of your access to the Platform and, to the extent necessary,
                shall survive termination or suspension in accordance with their nature.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            20. GOVERNING LAW
          </Title>

          <Text lh={1.6}>
            These Terms shall be governed by and construed in accordance with the laws of India, and courts at Pune,
            Maharashtra, India therein shall have exclusive jurisdiction in relation to any disputed arising out of or
            in connection with these Terms.
          </Text>

          <Title order={3} mt="xl">
            CONTACT US
          </Title>

          <Text lh={1.6}>
            For questions or concerns regarding these Terms, please contact Company at{" "}
            <strong>
              <em>
                <u>info@gtsetu.com</u>
              </em>
            </strong>{" "}
            or through the Platform's official communication channels.
          </Text>
        </Box>
      </Container>
    </Box>
  );
}
